function renderBuildComponentSourceContentTemplate({ pPascal }: { pPascal: string }): string {
  return `    <NuqsAdapter>
      <React.Suspense fallback={null}>
        <${pPascal}FormInner />
      </React.Suspense>
    </NuqsAdapter>`
}

function renderBuildComponentSourceGeneratedFunctionTemplate({
  pPascal,
  providerOpen
}: {
  pPascal: string
  providerOpen: string
}): string {
  return `\nexport function ${pPascal}Form() {
  return (
${providerOpen}
  )
}\n`
}

function renderBuildComponentSourceClientModuleTemplate({
  pHasListFieldsTrash2,
  pRhfImport,
  pPascal,
  pActionImportPath,
  pZodFields,
  fieldErrorHelper,
  pStepsConst,
  pDefaults,
  pFieldArraySetup,
  pWatchSetup,
  pSuccessHandler,
  pSuccessMessage,
  pStepContentBlocks,
  pSubmitText,
  exportWrapper
}: {
  pHasListFieldsTrash2: string
  pRhfImport: string
  pPascal: string
  pActionImportPath: string
  pZodFields: string
  fieldErrorHelper: string
  pStepsConst: string
  pDefaults: string
  pFieldArraySetup: string
  pWatchSetup: string
  pSuccessHandler: string
  pSuccessMessage: string
  pStepContentBlocks: string
  pSubmitText: string
  exportWrapper: string
}): string {
  return `'use client'

import * as React from 'react'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { ChevronLeft, ChevronRight${pHasListFieldsTrash2} } from 'lucide-react'
import { createParser, useQueryState } from 'nuqs'
import { NuqsAdapter } from 'nuqs/adapters/next/app'
${pRhfImport}
import { z } from 'zod/v3'
import { create${pPascal}Submission } from '@admin/actions/${pActionImportPath}'

const formSchema = z.object({
${pZodFields}
})

type FormValues = z.infer<typeof formSchema>
${fieldErrorHelper}

${pStepsConst}

const stepParser = createParser({
  parse(value) {
    if (!/^\\d+$/.test(value)) {
      return null
    }
    const step = Number.parseInt(value, 10)
    if (!Number.isInteger(step) || step < 0 || step >= STEPS.length) {
      return null
    }
    return step
  },
  serialize(value) {
    return String(value)
  }
}).withDefault(0)

function ${pPascal}FormInner() {
  const [currentStep, setCurrentStep] = useQueryState('step', stepParser)
  const [submitted, setSubmitted] = React.useState(false)
  const [submitting, startSubmitTransition] = React.useTransition()

  const form = useForm<FormValues>({
    resolver: standardSchemaResolver(formSchema),
    defaultValues: {
${pDefaults}
    },
  })

${pFieldArraySetup}${pWatchSetup}
  async function handleNext() {
    const stepFields = STEPS[currentStep].fields as (keyof FormValues)[]
    const isValid = await form.trigger(stepFields, { shouldFocus: true })
    if (isValid) {
      setCurrentStep(currentStep + 1)
    }
  }

  function handleBack() {
    setCurrentStep(currentStep - 1)
  }

  function onSubmit(values: FormValues) {
    startSubmitTransition(async () => {
      try {
        const result = await create${pPascal}Submission(values)
        if (result.success) {
          ${pSuccessHandler}
        } else {
          form.setError('root', { message: result.error || 'Something went wrong' })
        }
      } catch {
        form.setError('root', { message: 'Something went wrong. Please try again.' })
      }
    })
  }

  if (submitted) {
    return (
      <div className="rounded-sm border p-6 text-center">
        <h3 className="text-lg font-semibold">Thank you!</h3>
        <p className="mt-2 text-muted-foreground">${pSuccessMessage}</p>
      </div>
    )
  }

  return (
    <form onSubmit={(event) => event.preventDefault()} className="space-y-8">
        <div>
          <h3 className="text-lg font-semibold">{STEPS[currentStep].label}</h3>
          {'description' in STEPS[currentStep] && STEPS[currentStep].description && (
            <p className="text-sm text-muted-foreground mt-1">{STEPS[currentStep].description}</p>
          )}
        </div>

        <div key={currentStep} className="animate-in fade-in duration-300 space-y-6">
${pStepContentBlocks}
        </div>

        {form.formState.errors.root && (
          <p className="text-sm text-destructive">{form.formState.errors.root.message}</p>
        )}

        <div className="flex justify-between">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronLeft className="size-4" />
            Back
          </button>
          {currentStep < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              Next
              <ChevronRight className="ml-2 size-4" />
            </button>
          ) : (
            <button
              type="button"
              disabled={submitting}
              aria-busy={submitting}
              onClick={() => form.handleSubmit(onSubmit)()}
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-xs transition-colors hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
            >
              {submitting ? 'Submitting…' : ${pSubmitText}}
            </button>
          )}
        </div>
      </form>
  )
}
${exportWrapper}`
}

export function renderFormPipelineBuildComponentSourceRenderer(
  request:
    | {
        kind: 'renderBuildComponentSourceContentTemplate'
        props: {
          pPascal: string
        }
      }
    | {
        kind: 'renderBuildComponentSourceGeneratedFunctionTemplate'
        props: {
          pPascal: string
          providerOpen: string
        }
      }
    | {
        kind: 'renderBuildComponentSourceClientModuleTemplate'
        props: {
          pHasListFieldsTrash2: string
          pRhfImport: string
          pPascal: string
          pActionImportPath: string
          pZodFields: string
          fieldErrorHelper: string
          pStepsConst: string
          pDefaults: string
          pFieldArraySetup: string
          pWatchSetup: string
          pSuccessHandler: string
          pSuccessMessage: string
          pStepContentBlocks: string
          pSubmitText: string
          exportWrapper: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderBuildComponentSourceContentTemplate':
      return renderBuildComponentSourceContentTemplate(request.props)
    case 'renderBuildComponentSourceGeneratedFunctionTemplate':
      return renderBuildComponentSourceGeneratedFunctionTemplate(request.props)
    case 'renderBuildComponentSourceClientModuleTemplate':
      return renderBuildComponentSourceClientModuleTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
